// recipe-import.ts
import { Recipe } from '../types';
import { toast } from 'react-hot-toast';

/**
 * Helper function to infer a recipe name from a given URL.
 * It extracts the last part of the pathname, replaces hyphens/underscores with spaces,
 * and title-cases the words.
 */
function inferRecipeName(url: string): string {
  try {
    const urlObj = new URL(url);
    const segments = urlObj.pathname.split('/').filter(Boolean);
    let inferredName = '';

    if (segments.length) {
      // Use the last segment of the URL, for example "spaghetti-carbonara"
      inferredName = segments[segments.length - 1];
      // Replace hyphens and underscores with spaces
      inferredName = inferredName.replace(/[-_]/g, ' ');
      // Capitalize the first letter of each word
      inferredName = inferredName.replace(/\b\w/g, (char) => char.toUpperCase());
    } else {
      // Fallback to using the hostname if no path segments exist
      inferredName = urlObj.hostname;
    }

    return inferredName;
  } catch (error) {
    console.error('Error inferring recipe name:', error);
    return 'Imported Recipe';
  }
}

export async function importRecipeFromUrl(url: string): Promise<Partial<Recipe>> {
  try {
    // Basic URL validation
    if (!url) {
      throw new Error('Please enter a recipe URL');
    }
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      throw new Error('Please enter a valid URL starting with http:// or https://');
    }

    // Simulate API delay (replace with real API call when available)
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Derive the recipe name from the URL
    const inferredName = inferRecipeName(url);

    // Dummy recipe data for testing
    const dummyRecipe: Partial<Recipe> = {
      name: inferredName, // Automatically populated based on URL
      ingredients: [
        { amount: 1, unit: 'cup', name: 'Flour' },
        { amount: 2, unit: 'pcs', name: 'Eggs' },
        { amount: 0.5, unit: 'cup', name: 'Milk' },
      ],
      instructions: [
        'Preheat oven to 350°F (175°C).',
        'Mix all ingredients in a bowl.',
        'Bake for 25 minutes.',
      ],
      prepTime: 15,
      cookTime: 25,
      servings: 4,
      imageUrl: '',
      source: url,
      tags: ['imported', 'test'],
      isPublic: false,
    };

    toast.success('Recipe imported successfully!');
    return dummyRecipe;
  } catch (error: any) {
    console.error('Error importing recipe:', error.message);
    toast.error(`Import failed: ${error.message}`);
    throw error;
  }
}
